// useRealtimeRefresh — the standard way a component goes "live" off the
// WebSocket instead of polling.
//
//   useRealtimeRefresh(
//     (msg) => msg.event && msg.event.indexOf('ingestion.execution.') === 0,
//     () => loadExecutions(),
//   );
//
// It calls `onRefresh(msg)` whenever a realtime event matching `match(msg)`
// arrives, and ALSO once whenever the socket (re)connects (status 'open') so a
// window missed while the socket was down is reconciled (components already
// fetch on mount, so this only matters for a disconnect mid-view). Listening is
// scoped to the mounted component (the effect unsubscribes on unmount), so
// unlike a central saga dispatch this never fires refreshes for screens that
// aren't open.
//
// `match`/`onRefresh` are read through refs, so they can close over fresh props
// each render without re-subscribing. Pass a stable `deps` array only if you
// need the listener torn down/rebuilt (rarely needed).
//
// Events arrive on whichever channels the client is subscribed to: the user
// channel + the active workspace channel are subscribed at connect, and goal
// pages add `goal:<id>` while mounted. So a workspace-scoped event (e.g.
// `ingestion.execution.completed`) reaches any mounted listener for free.
//
// Coalescing: refreshes are debounced (trailing, `debounceMs`, default 350ms)
// so a BURST of matching events — e.g. a tournament fitting 6 models fires a
// stream of `goal.tournament.*` events — collapses into a single refetch
// instead of one request per event. Live latency is still sub-second.
//
// `onRefresh` receives the LAST matching message of the coalesced burst (or
// `null` for a reconnect-triggered refresh). Pass `{ debounceMs: 0 }` to
// refresh on every event without coalescing.

import { useEffect, useRef } from 'react';
import realtimeClient from './wsClient';

export default function useRealtimeRefresh(match, onRefresh, deps = [], options = {}) {
  const { debounceMs = 350 } = options;
  const matchRef = useRef(match);
  const refreshRef = useRef(onRefresh);
  matchRef.current = match;
  refreshRef.current = onRefresh;

  useEffect(() => {
    let timer = null;
    let lastMsg = null;

    const fire = () => {
      try { refreshRef.current(lastMsg); } catch (e) { /* non-fatal */ }
    };
    const schedule = (msg) => {
      lastMsg = msg;
      if (debounceMs <= 0) { fire(); return; }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; fire(); }, debounceMs);
    };

    const offEvent = realtimeClient.on('event', (msg) => {
      if (!msg || msg.type === 'error') return;
      try {
        if (matchRef.current(msg)) schedule(msg);
      } catch (e) { /* listener errors are non-fatal */ }
    });
    // Only reconcile on genuine RE-connects, not the initial connect: the
    // component's own mount effect already does the first load, so refreshing
    // on the first 'open' would double-load every page (mount fetch + open
    // fetch). Seed from the current status so an already-open client (SPA
    // navigation) is treated as "already connected".
    let hasConnected = realtimeClient.status === 'open';
    const offStatus = realtimeClient.on('status', (status) => {
      if (status !== 'open') return;
      if (!hasConnected) { hasConnected = true; return; }
      schedule(null);
    });
    return () => {
      if (timer) clearTimeout(timer);
      offEvent();
      offStatus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Convenience matcher: event code starts with one of the given prefixes.
export function eventPrefix(...prefixes) {
  return (msg) =>
    typeof msg.event === 'string' && prefixes.some((p) => msg.event.indexOf(p) === 0);
}
